import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { UTILS__image } from '@/utils/UTILS__image';
import { path_relative_to_absolute } from '@/utils/utils_files_client';
import { assert_not_null } from '@shared/asserts/utils_asserts';
import { DTO__image_size } from '@shared/dto/+image/image_asset_base/DTO__image_size';
import { useMemo, useState } from 'react';
import * as THREE from 'three';
import { ConstantsOpenPose } from '../constants_open_pose';
import { EThreejsStub } from '../enums/e_threejs_stub';

export function use_open_pose_billboard_texture() {
  /*
   * FIELDS
   */

  const { generation_current } = use_slice_generation(['generation_current']);
  const { page_generation_advanced_base_image_draw_data, page_generation_advanced_base_image_generation_area_data } = use_slice_ui([
    'page_generation_advanced_base_image_draw_data',
    'page_generation_advanced_base_image_generation_area_data',
  ]);

  const reference_image_size = useMemo(() => {
    if (generation_current.reference_image.is_stub()) {
      return DTO__image_size.CREATE_STUB();
    } else {
      return generation_current.reference_image.image_size;
    }
  }, [generation_current.reference_image.image_size]);

  const reference_image_absolute_path = useMemo(
    () => (generation_current.reference_image.url_relative_cropped ? path_relative_to_absolute(generation_current.reference_image.url_relative_cropped) : ''),
    [generation_current.reference_image.url_relative_cropped],
  );

  const { width, height } = useMemo(() => {
    const target_width_pixels = ConstantsOpenPose.BILLBOARD_DIMENSIONS_WIDTH; // BILLBOARD_DIMENSIONS_WIDTH

    const target_height_pixels = !reference_image_size.is_stub()
      ? target_width_pixels / reference_image_size.aspect_ratio
      : ConstantsOpenPose.BILLBOARD_DIMENSIONS_HEIGHT; // fallback

    return { width: Math.round(target_width_pixels), height: Math.round(target_height_pixels) };
  }, [reference_image_size]);

  const [canvas_texture, set_canvas_texture] = useState<THREE.CanvasTexture>(EThreejsStub.CANVAS_TEXTURE);

  /*
   * EFFECTS
   */

  use_effect(() => {
    let previous_texture: THREE.CanvasTexture = EThreejsStub.CANVAS_TEXTURE;

    (async () => {
      const base_image_element = await UTILS__image.load_from_url(reference_image_absolute_path);

      if (!base_image_element) return set_canvas_texture(new THREE.CanvasTexture());

      const draw_overlay_image_element = await UTILS__image.load_from_url(page_generation_advanced_base_image_draw_data);
      const generation_area_image_element = await UTILS__image.load_from_url(page_generation_advanced_base_image_generation_area_data);

      const canvas_element = document.createElement('canvas');
      canvas_element.width = width;
      canvas_element.height = height;

      const canvas_2d_context = canvas_element.getContext('2d', { willReadFrequently: true });
      assert_not_null(canvas_2d_context);
      canvas_2d_context.imageSmoothingEnabled = false;
      canvas_2d_context.clearRect(0, 0, width, height);

      canvas_2d_context.drawImage(base_image_element, 0, 0, width, height);
      const base_image_data = canvas_2d_context.getImageData(0, 0, width, height);
      const base_rgba = base_image_data.data;

      const binary_generation_mask = generation_area_image_element
        ? make_binary_mask_from_image(generation_area_image_element, width, height, ConstantsOpenPose.BILLBOARD_MASK_ALPHA_THRESHOLD)
        : new Uint8Array(width * height);

      let blurred_image_data: ImageData | null = null;

      if (generation_area_image_element) {
        const blur_canvas_element = document.createElement('canvas');
        blur_canvas_element.width = width;
        blur_canvas_element.height = height;

        const blur_canvas_context = blur_canvas_element.getContext('2d')!;
        blur_canvas_context.imageSmoothingEnabled = false;
        blur_canvas_context.filter = `blur(${ConstantsOpenPose.BILLBOARD_BLUR_PX}px)`;
        blur_canvas_context.drawImage(base_image_element, 0, 0, width, height);

        blurred_image_data = blur_canvas_context.getImageData(0, 0, width, height);
      }

      const blurred_rgba = blurred_image_data ? blurred_image_data.data : null;

      const inner_edge_mask = generation_area_image_element ? compute_inner_edge(binary_generation_mask, width, height) : new Uint8Array(width * height);
      const border_mask =
        ConstantsOpenPose.BILLBOARD_BORDER_WIDTH > 1
          ? dilate_circle_mask(inner_edge_mask, width, height, ConstantsOpenPose.BILLBOARD_BORDER_WIDTH - 1)
          : inner_edge_mask;

      const result_image_data = canvas_2d_context.createImageData(width, height);
      const result_rgba = result_image_data.data;

      for (let pixel_index = 0; pixel_index < width * height; pixel_index++) {
        const channel_index = pixel_index * 4;

        let red = base_rgba[channel_index + 0];
        let green = base_rgba[channel_index + 1];
        let blue = base_rgba[channel_index + 2];

        if (binary_generation_mask[pixel_index] === 1 && blurred_rgba) {
          red = blurred_rgba[channel_index + 0];
          green = blurred_rgba[channel_index + 1];
          blue = blurred_rgba[channel_index + 2];
        }

        result_rgba[channel_index + 0] = red;
        result_rgba[channel_index + 1] = green;
        result_rgba[channel_index + 2] = blue;
        result_rgba[channel_index + 3] = 255;
      }

      if (draw_overlay_image_element) {
        const overlay_canvas_element = document.createElement('canvas');
        overlay_canvas_element.width = width;
        overlay_canvas_element.height = height;

        const overlay_context = overlay_canvas_element.getContext('2d', { willReadFrequently: true })!;
        overlay_context.imageSmoothingEnabled = false;
        overlay_context.clearRect(0, 0, width, height);
        overlay_context.drawImage(draw_overlay_image_element, 0, 0, width, height);

        const overlay_rgba = overlay_context.getImageData(0, 0, width, height).data;

        for (let pixel_index = 0; pixel_index < width * height; pixel_index++) {
          if (border_mask[pixel_index] === 1) continue;

          const channel_index = pixel_index * 4;
          const overlay_alpha = overlay_rgba[channel_index + 3];
          if (overlay_alpha === 0) continue;

          const source_alpha = binary_generation_mask[pixel_index] === 1 ? 1.0 : 0.7;

          const source_red = overlay_rgba[channel_index + 0];
          const source_green = overlay_rgba[channel_index + 1];
          const source_blue = overlay_rgba[channel_index + 2];

          const destination_red = result_rgba[channel_index + 0];
          const destination_green = result_rgba[channel_index + 1];
          const destination_blue = result_rgba[channel_index + 2];

          const output_red = source_red * source_alpha + destination_red * (1 - source_alpha);
          const output_green = source_green * source_alpha + destination_green * (1 - source_alpha);
          const output_blue = source_blue * source_alpha + destination_blue * (1 - source_alpha);

          result_rgba[channel_index + 0] = output_red | 0;
          result_rgba[channel_index + 1] = output_green | 0;
          result_rgba[channel_index + 2] = output_blue | 0;
          result_rgba[channel_index + 3] = 255;
        }
      }

      if (generation_area_image_element) {
        const stripe_period = ConstantsOpenPose.BILLBOARD_STRIPE_WIDTH + ConstantsOpenPose.BILLBOARD_STRIPE_GAP;

        for (let pixel_index = 0; pixel_index < width * height; pixel_index++) {
          if (border_mask[pixel_index] !== 1) continue;

          const channel_index = pixel_index * 4;
          const x = pixel_index % width;
          const y = (pixel_index / width) | 0;
          const diagonal = (x + y) % stripe_period;

          if (diagonal < ConstantsOpenPose.BILLBOARD_STRIPE_WIDTH) {
            // BLUE #2355B1
            result_rgba[channel_index + 0] = 0x23;
            result_rgba[channel_index + 1] = 0x55;
            result_rgba[channel_index + 2] = 0xb1;
            result_rgba[channel_index + 3] = 255;
          } else {
            // YELLOW #F8D849
            result_rgba[channel_index + 0] = 0xf8;
            result_rgba[channel_index + 1] = 0xd8;
            result_rgba[channel_index + 2] = 0x49;
            result_rgba[channel_index + 3] = 255;
          }
        }
      }

      canvas_2d_context.putImageData(result_image_data, 0, 0);

      const new_canvas_texture = new THREE.CanvasTexture(canvas_element);
      new_canvas_texture.needsUpdate = true;
      new_canvas_texture.magFilter = THREE.LinearFilter;
      new_canvas_texture.minFilter = THREE.LinearFilter;

      if (previous_texture !== EThreejsStub.CANVAS_TEXTURE) previous_texture.dispose();

      previous_texture = new_canvas_texture;

      set_canvas_texture(new_canvas_texture);
    })();
  }, [reference_image_absolute_path, page_generation_advanced_base_image_draw_data, page_generation_advanced_base_image_generation_area_data, width, height]);

  /*
   * METHODS
   */

  function make_binary_mask_from_image(image_element: HTMLImageElement, image_width: number, image_height: number, threshold: number): Uint8Array {
    const canvas_element = document.createElement('canvas');

    canvas_element.width = image_width;
    canvas_element.height = image_height;

    const canvas_2d_context = canvas_element.getContext('2d')!;
    canvas_2d_context.imageSmoothingEnabled = false;
    canvas_2d_context.clearRect(0, 0, image_width, image_height);
    canvas_2d_context.drawImage(image_element, 0, 0, image_width, image_height);

    const context_image_data = canvas_2d_context.getImageData(0, 0, image_width, image_height).data;

    const mask = new Uint8Array(image_width * image_height);

    for (let pixel_index = 0; pixel_index < image_width * image_height; pixel_index++) {
      const alpha = context_image_data[pixel_index * 4 + 3];
      mask[pixel_index] = alpha > threshold ? 1 : 0;
    }

    return mask;
  }

  function compute_inner_edge(mask: Uint8Array, image_width: number, image_height: number): Uint8Array {
    const edge_mask = new Uint8Array(image_width * image_height);

    for (let y = 0; y < image_height; y++) {
      for (let x = 0; x < image_width; x++) {
        const linear_index = y * image_width + x;
        if (mask[linear_index] === 0) continue;

        let is_edge = 0;

        for (const [delta_x, delta_y] of ConstantsOpenPose.BILLBOARD_EIGHT_NEIGHBOR_OFFSETS) {
          const neighbor_x = x + delta_x;
          const neighbor_y = y + delta_y;

          if (neighbor_x < 0 || neighbor_y < 0 || neighbor_x >= image_width || neighbor_y >= image_height) {
            is_edge = 1;
            break;
          }

          if (mask[neighbor_y * image_width + neighbor_x] === 0) {
            is_edge = 1;
            break;
          }
        }

        if (is_edge) edge_mask[linear_index] = 1;
      }
    }

    return edge_mask;
  }

  function dilate_circle_mask(source_mask: Uint8Array, image_width: number, image_height: number, radius_pixels: number): Uint8Array {
    if (radius_pixels <= 0) return source_mask.slice();

    const output_mask = new Uint8Array(image_width * image_height);
    const radius_squared = radius_pixels * radius_pixels;

    for (let y = 0; y < image_height; y++) {
      for (let x = 0; x < image_width; x++) {
        let has_hit = 0;

        for (let delta_y = -radius_pixels; delta_y <= radius_pixels && !has_hit; delta_y++) {
          const neighbor_y = y + delta_y;
          if (neighbor_y < 0 || neighbor_y >= image_height) continue;

          for (let delta_x = -radius_pixels; delta_x <= radius_pixels; delta_x++) {
            const neighbor_x = x + delta_x;
            if (neighbor_x < 0 || neighbor_x >= image_width) continue;

            if (delta_x * delta_x + delta_y * delta_y > radius_squared) continue;

            if (source_mask[neighbor_y * image_width + neighbor_x]) {
              has_hit = 1;
              break;
            }
          }
        }

        if (has_hit) output_mask[y * image_width + x] = 1;
      }
    }

    return output_mask;
  }

  /*
   * RETURN API
   */

  return { texture: canvas_texture, width, height };
}
